/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useRef } from 'react';
import { m } from 'motion/react';
import { X, PlugZap, Gauge, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SURFACES, trackEvent } from '../lib/gtag';
import { lockPageScroll } from '../lib/scrollLock';

interface SmartChargerInfoModalProps {
  open: boolean;
  onClose: () => void;
  onOpenMidChecker?: () => void;
}

/**
 * "Slimme laadpaal & MID-meter" explainer, opened from the per-card
 * "Slimme laadpaal vereist?" label (ComparisonEngine + ProviderProfilePage).
 *
 * Built on the native <dialog> so its copy is ALWAYS in the DOM: this
 * component is prerendered with the comparison engine, and the previous
 * `{open && …}` body left both explanatory paragraphs out of the static HTML
 * — invisible to non-JS crawlers and LLM readers. A closed <dialog> is
 * display:none via the UA stylesheet (not the `hidden` attribute, which text
 * extractors treat as "drop this") and sits outside the accessibility tree
 * until showModal().
 *
 * showModal() also covers what the hand-rolled focus trap used to do — top
 * layer, focus containment, Escape, the page behind made inert — plus the one
 * thing it never did: focus returns to the label that opened the dialog when
 * it closes.
 *
 * The two bodies and the MID heading read NEW `smart_charger_modal.*_v2` keys
 * (2026-10-07): the old `table.smart_charging_info_what_body` / `_mid_*` said
 * a MID meter is "vrijwel altijd verplicht" and left the impression that a
 * smart charger is a NEa requirement. What the NEa requires is a MID meter
 * built into the charger and a link with the charger that can be shown; a
 * connection (OCPP, app, cloud) is the provider's own condition. The old
 * values are frozen in i18n_admin on acc and prd, so the fix is a new key
 * (docs/i18n-masking.md). This copy is code-owned, as it always was.
 */
export const SmartChargerInfoModal: React.FC<SmartChargerInfoModalProps> = ({
  open,
  onClose,
  onOpenMidChecker,
}) => {
  const { t, language } = useLanguage();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!open) return;
    trackEvent('smart_charger_info_open', { surface: SURFACES.smartChargerModal, language });
  }, [open, language]);

  // Mirror the `open` prop into the native modal state. Closing is immediate
  // (no exit animation): the dialog must leave the top layer the moment the
  // parent flips `open`, so the MID-checker modal can take over right after.
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) {
      // jsdom (unit tests) has no showModal(); fall back to the plain attribute.
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);

  // Scroll-lock the page behind: a modal dialog makes it inert, but the wheel
  // still scrolls it.
  useEffect(() => {
    if (!open) return;
    return lockPageScroll();
  }, [open]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="smart-charger-info-title"
      // Native close paths (Escape, close()) fire `close` — keep the parent in sync.
      onClose={onClose}
      // A click on ::backdrop is dispatched to the <dialog> itself, while any
      // click inside the card targets one of its descendants — so "target is
      // the dialog" means "backdrop click". Preflight zeroes every margin, so
      // `m-auto` is what centres the box in the top layer.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      // No backdrop blur below md: a full-screen backdrop-filter is a repaint
      // per frame on a phone GPU (see modalLayout.tsx); the veil is a touch
      // denser there instead. `dvh`, not `vh`: the panel has to fit the screen
      // as it is with the browser's toolbars showing, or its last row sits
      // behind them.
      className="m-auto w-[calc(100%-2rem)] max-w-md max-h-none overflow-visible rounded-3xl border-0 bg-transparent p-0 backdrop:bg-slate-950/85 md:backdrop:bg-slate-950/80 md:backdrop:backdrop-blur-md"
    >
      <m.div
        initial={false}
        animate={open ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 24, scale: 0.97 }}
        transition={{ type: 'spring', damping: 25, stiffness: 220 }}
        className="relative w-full bg-slate-900 border border-emerald-500/20 rounded-3xl shadow-2xl shadow-emerald-900/30 max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <PlugZap className="w-5 h-5 text-emerald-400" />
            </div>
            <h2
              id="smart-charger-info-title"
              className="text-base md:text-lg font-black text-white leading-tight mt-1"
            >
              {t('table.smart_charging_info_title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('table.smart_charging_info_close')}
            className="shrink-0 w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-emerald-300 mb-2">
              {t('table.smart_charging_info_what_title')}
            </h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              {t('smart_charger_modal.what_body_v2')}
            </p>
          </div>

          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4">
            <h3 className="text-xs font-black uppercase tracking-widest text-emerald-300 mb-2">
              {t('smart_charger_modal.mid_title_v2')}
            </h3>
            <p className="text-sm text-slate-200 leading-relaxed">
              {t('smart_charger_modal.mid_body_v2')}
            </p>
            {onOpenMidChecker && (
              <button
                type="button"
                onClick={() => {
                  trackEvent('smart_charger_info_cta', {
                    surface: SURFACES.smartChargerModal,
                    language,
                  });
                  onOpenMidChecker();
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-black text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline"
              >
                <Gauge className="w-4 h-4" aria-hidden="true" />
                {t('mid_checker.trigger_label')}
                <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-black shadow-lg shadow-emerald-900/40 transition-colors"
          >
            {t('table.smart_charging_info_close')}
          </button>
        </div>
      </m.div>
    </dialog>
  );
};
